import { ArrowRight, ExternalLink, Landmark, Clock, CalendarClock, FilePenLine, FileX, SearchCheck, UserCheck, TrendingDown } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { GoldButton } from "../components/Buttons";
import { Reveal } from "../components/Reveal";
import { TAXPAYER_TOOLS, REFUND_GUIDE } from "../data/content";

export default function Resources() {
    return (
        <div data-testid="page-resources">
            <Seo
                title="Taxpayer Tools & Resources | Freedom Tax Filer"
                description="Access official IRS tools to check your refund, track an amended return, obtain tax records, and make federal tax payments."
                path="/resources"
            />

            <section className="pt-32 pb-20 bg-[#172337] border-b border-white/10" data-testid="resources-hero">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow-dark">Official IRS Resources</p>
                        <div className="gold-rule mt-5" />
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-6 leading-[1.06] tracking-tight max-w-3xl">
                            Taxpayer Tools &amp; <em className="italic text-gold-gradient">Resources.</em>
                        </h1>
                        <p className="text-white/65 text-base sm:text-lg leading-relaxed mt-6 max-w-2xl" data-testid="resources-subtitle">
                            {TAXPAYER_TOOLS.subtitle}
                        </p>
                        <div className="inline-flex items-center gap-2 mt-8 border border-[#C8A45D]/40 bg-[#C8A45D]/10 rounded-full px-4 py-2 text-[#E4D2A3] text-[11px] uppercase tracking-[0.18em]" data-testid="resources-badge">
                            <Landmark className="h-3.5 w-3.5" /> {TAXPAYER_TOOLS.badge}
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad bg-[#FAFAF7]" data-testid="resources-section">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-5xl">
                        {TAXPAYER_TOOLS.cards.map((card, i) => {
                            const Icon = card.icon;
                            return (
                                <Reveal key={card.title} delay={i * 0.07} className="h-full">
                                    <div
                                        className="h-full flex flex-col bg-white border border-[#E7E4DC] rounded-xl p-8 shadow-sm hover:border-[#C8A45D]/60 hover:shadow-lg transition-[border-color,box-shadow] duration-300"
                                        data-testid={`tool-card-${i}`}
                                    >
                                        <span className="h-12 w-12 rounded-lg border border-[#C8A45D]/50 bg-[#C8A45D]/10 flex items-center justify-center">
                                            <Icon className="h-5 w-5 text-[#8A6D2F]" />
                                        </span>
                                        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A6D2F] font-semibold mt-5">Official IRS Tool</p>
                                        <h2 className="font-display text-2xl text-[#20242B] mt-2 leading-snug" data-testid={`tool-title-${i}`}>
                                            {card.title}
                                        </h2>
                                        <p className="text-[#737985] text-sm leading-relaxed mt-3 flex-1" data-testid={`tool-desc-${i}`}>
                                            {card.description}
                                        </p>
                                        <GoldButton asChild className="h-11 px-6 text-[12px] mt-6 self-start">
                                            <a
                                                href={card.url}
                                                target="_blank"
                                                rel="noopener noreferrer external"
                                                data-testid={`tool-link-${i}`}
                                                aria-label={`${card.button} on the official IRS website (opens in a new tab)`}
                                            >
                                                {card.button} <ExternalLink className="h-3.5 w-3.5" />
                                            </a>
                                        </GoldButton>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>

                    <Reveal delay={0.05}>
                        <div className="max-w-5xl mt-16" data-testid="refund-guide">
                            <p className="text-[#8A6D2F] text-[12px] uppercase tracking-[0.2em]">{REFUND_GUIDE.eyebrow}</p>
                            <h2 className="font-display text-3xl sm:text-4xl text-[#20242B] tracking-tight leading-tight mt-4">
                                {REFUND_GUIDE.titleA} <em className="italic text-[#8A6D2F]">{REFUND_GUIDE.titleB}</em>
                            </h2>
                            <p className="text-[#737985] leading-relaxed mt-4 max-w-2xl" data-testid="refund-guide-intro">
                                {REFUND_GUIDE.intro}
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-9">
                                {REFUND_GUIDE.timeline.map((t, i) => {
                                    const Icon = t.icon;
                                    return (
                                        <div key={t.title} className="bg-[#172337] border border-[#C8A45D]/40 rounded-xl p-6" data-testid={`refund-timeline-${i}`}>
                                            <span className="h-10 w-10 rounded-lg border border-[#C8A45D]/50 flex items-center justify-center">
                                                <Icon className="h-4 w-4 text-[#E4D2A3]" />
                                            </span>
                                            <h3 className="font-display text-lg text-white mt-4">{t.title}</h3>
                                            <p className="text-white/60 text-[13px] leading-relaxed mt-2">{t.text}</p>
                                        </div>
                                    );
                                })}
                            </div>

                            <h3 className="font-display text-xl sm:text-2xl text-[#20242B] mt-14">{REFUND_GUIDE.delaysTitle}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 mt-7">
                                {REFUND_GUIDE.delays.map((d, i) => {
                                    const Icon = d.icon;
                                    return (
                                        <div key={d.title} className="flex items-start gap-4" data-testid={`refund-delay-${i}`}>
                                            <span className="h-10 w-10 rounded-lg border border-[#C8A45D]/50 bg-[#C8A45D]/10 flex items-center justify-center shrink-0">
                                                <Icon className="h-4 w-4 text-[#8A6D2F]" />
                                            </span>
                                            <span>
                                                <span className="block text-[#20242B] font-semibold text-sm sm:text-base">{d.title}</span>
                                                <span className="block text-[#737985] text-sm leading-relaxed mt-1">{d.text}</span>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mt-12 border-t border-[#E7E4DC] pt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                                <p className="text-[#737985] text-[13px] leading-relaxed max-w-2xl" data-testid="refund-guide-note">{REFUND_GUIDE.note}</p>
                                <a
                                    href={REFUND_GUIDE.officialUrl}
                                    target="_blank"
                                    rel="noopener noreferrer external"
                                    data-testid="refund-guide-irs-link"
                                    aria-label="Official IRS refund help on IRS.gov (opens in a new tab)"
                                    className="shrink-0 inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.14em] text-[#8A6D2F] border-b border-[#C8A45D]/50 pb-1 hover:border-[#C8A45D] transition-colors duration-200"
                                >
                                    {REFUND_GUIDE.officialLabel} <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div
                            className="mt-16 max-w-5xl border border-[#C8A45D]/40 bg-[#172337] rounded-2xl p-8 sm:p-10 flex flex-col sm:flex-row gap-6"
                            data-testid="resources-disclaimer"
                        >
                            <Landmark className="h-6 w-6 text-[#C8A45D] shrink-0 mt-1" />
                            <p className="text-white/60 text-[13px] leading-relaxed">{TAXPAYER_TOOLS.disclaimer}</p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div
                            className="mt-12 max-w-5xl relative border border-[#C8A45D]/40 bg-[#111318] rounded-2xl px-8 sm:px-14 py-14 overflow-hidden shadow-xl"
                            data-testid="resources-cta"
                        >
                            <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-transparent via-[#C8A45D] to-transparent" />
                            <h3 className="font-display text-3xl sm:text-4xl text-white leading-tight max-w-xl">
                                Need Help Beyond These <em className="italic text-gold-gradient">Tools?</em>
                            </h3>
                            <p className="text-white/60 leading-relaxed mt-4 max-w-xl">{TAXPAYER_TOOLS.ctaText}</p>
                            <div className="mt-9">
                                <GoldButton asChild data-testid="resources-cta-button">
                                    <Link to="/contact">
                                        {TAXPAYER_TOOLS.ctaButton} <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </GoldButton>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
